// v2 counterpart of useMakeEmbroideredCartItem.
import { useCallback } from 'react'

import { getCategoryKeyFromRawCategory } from '../../app/_components/pages/organization/constants'
import { useOrganization } from '../../app/_providers/OrganizationProvider.client'
import { CartContainer, UseCartNewCartItem } from '../../brunswick/containers/cart'
import { useMakeCartItem } from '../../brunswick/containers/cart/utils'

import { V2EmbroideryVariants } from './providers/resolveV2EmbroideryVariants'
import { EmbroideryItem } from './providers/types'
import { buildV2EmbroideryLineSet, firstLogoItem } from './utils/buildV2EmbroideryLineSet'
import { hasLogoSetupLineForLogo } from './utils/unorganized'

export type MakeEmbroideredCartItemV2Input = {
  /** What the shopper has confirmed for the bag. Unchecked or unconfirmed items are already out. */
  items: readonly EmbroideryItem[]
  variants: V2EmbroideryVariants
  productColorHex: string | undefined
}

export const useMakeEmbroideredCartItemV2 = () => {
  const { cart } = CartContainer.useContainer()
  const { hasMandatoryLogoEmbroidery } = useOrganization()
  const makeCartItem = useMakeCartItem()

  return useCallback(
    (
      props: Parameters<ReturnType<typeof useMakeCartItem>>[0],
      categoryId: string,
      { items: itemsForBag, variants, productColorHex }: MakeEmbroideredCartItemV2Input
    ): UseCartNewCartItem[] => {
      if (itemsForBag.length === 0) return makeCartItem(props)

      const isB2bLogo = hasMandatoryLogoEmbroidery({
        categoryId,
        categoryKey: getCategoryKeyFromRawCategory(categoryId),
      })

      const lineSet = buildV2EmbroideryLineSet({
        items: itemsForBag,
        variants,
        productColorHex,
        logoSetupInCart: hasLogoSetupLineForLogo(
          cart.items,
          firstLogoItem(itemsForBag)?.values.logoFile?.id
        ),
      })

      const attributedPortalAs = (itemType: 'PortalLogo' | 'Other') =>
        props.options.attributedPortal ? { ...props.options.attributedPortal, itemType } : undefined

      const cartItems: UseCartNewCartItem[] = []

      lineSet.attachmentLines.forEach(({ variant, properties }) => {
        cartItems.push(
          ...makeCartItem({
            variant: { ...variant.productInfo },
            variantGroup: variant.productInfo.group,
            product: variant.productInfo.group.product,
            options: {
              properties,
              quantity: props.options.quantity,
              attributedPortal: attributedPortalAs(isB2bLogo ? 'PortalLogo' : 'Other'),
            },
          })
        )
      })

      const garmentOptions = {
        ...props.options,
        properties: {
          ...props.options.properties,
          ...lineSet.garmentProperties,
          // legacy-retained
          _embroideryId: 'item' in props ? props.item.shopifyId : props.variant.shopifyId,
        },
      }

      if ('item' in props) {
        cartItems.push(...makeCartItem({ ...props, finalSale: true, options: garmentOptions }))
      } else {
        cartItems.push(
          ...makeCartItem({
            ...props,
            variantGroup: { ...props.variantGroup, finalSale: true },
            options: garmentOptions,
          })
        )
      }

      if (lineSet.setupLine) {
        const { variant, properties } = lineSet.setupLine
        cartItems.push(
          ...makeCartItem({
            variant: { ...variant.productInfo },
            variantGroup: variant.productInfo.group,
            product: variant.productInfo.group.product,
            options: {
              properties,
              quantity: 1,
              attributedPortal: attributedPortalAs('Other'),
            },
          })
        )
      }

      return cartItems
    },
    [hasMandatoryLogoEmbroidery, cart.items, makeCartItem]
  )
}
